export interface YearlyProjection {
  year: number;
  projectedGenerationMwh: number;
  avoidedCo2eKg: number;
  avoidedDalys: number;
  avoidedPdfs: number;
  economicValueNominalUsd: number;
  economicValueNpvUsd: number;
}

export interface LifetimeProjectionConfig {
  projectLifetimeYears: number;
  socialDiscountRate: number;
  annualDegradationPct: number;
  degradationModel: string;
  includeConstructionImpacts: boolean;
  includeDecommissioningImpacts: boolean;
}

export interface LifetimeProjection {
  projectId: string;
  config: LifetimeProjectionConfig;
  codYear: number;
  baseYearGenerationMwh: number | null | undefined;
  baseYearCapacityFactor: number;
  impactRates: {
    dalysPerMwh: number;
    pdfsPerMwh: number;
    usdPerMwh: number;
    co2eKgPerMwh: number;
  };
  yearlyProjections: YearlyProjection[];
  lifetimeGenerationGwh: number;
  lifetimeAvoidedCo2eTonnes: number;
  lifetimeAvoidedDalys: number;
  lifetimeAvoidedPdfs: number;
  lifetimeEconomicNominalUsd: number;
  lifetimeEconomicNpvUsd: number;
  infrastructureCo2eTonnes: number | null;
  netLifetimeCo2eTonnes: number;
  calculatedAt: string;
  modelVersion: string;
}

export const DEFAULT_LIFETIME_CONFIG: Record<string, {
  projectLifetimeYears: number;
  annualDegradationPct: number;
  degradationModel: string;
}> = {
  solar_ground: {
    projectLifetimeYears: 25,
    annualDegradationPct: 0.005,
    degradationModel: "exponential",
  },
  solar_rooftop: {
    projectLifetimeYears: 25,
    annualDegradationPct: 0.005,
    degradationModel: "exponential",
  },
  wind_onshore: {
    projectLifetimeYears: 20,
    annualDegradationPct: 0.016,
    degradationModel: "exponential",
  },
};

export function generateLifetimeProjection(
  baseYearMwh: number,
  impactRates: Record<string, number>,
  config: LifetimeProjectionConfig,
  codYear: number
): YearlyProjection[] {
  const projections: YearlyProjection[] = [];

  for (let i = 0; i < config.projectLifetimeYears; i++) {
    // Apply degradation
    const degradationFactor =
      config.degradationModel === "exponential"
        ? Math.pow(1 - config.annualDegradationPct, i)
        : Math.max(0, 1 - config.annualDegradationPct * i);

    const projectedMwh = baseYearMwh * degradationFactor;

    // Calculate impacts using per-MWh rates
    const avoidedCo2eKg = projectedMwh * (impactRates.co2eKgPerMwh || 0);
    const avoidedDalys = projectedMwh * (impactRates.dalysPerMwh || 0);
    const avoidedPdfs = projectedMwh * (impactRates.pdfsPerMwh || 0);
    const economicNominal = projectedMwh * (impactRates.usdPerMwh || 0);

    // NPV discount factor: 1 / (1 + r)^i
    const discountFactor = Math.pow(1 + config.socialDiscountRate, -i);
    const economicNpv = economicNominal * discountFactor;

    projections.push({
      year: codYear + i,
      projectedGenerationMwh: projectedMwh,
      avoidedCo2eKg,
      avoidedDalys,
      avoidedPdfs,
      economicValueNominalUsd: economicNominal,
      economicValueNpvUsd: economicNpv,
    });
  }

  return projections;
}
